import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { PageHeader, StatCard, TableWrap, Th, Td, Loading, EmptyState, exportCSV } from "../components/shared";
import { formatINR, formatDate } from "../lib/format";
import { Button } from "../components/ui/button";
import { ArrowLeft, Download } from "lucide-react";

export default function AccountDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/accounts/${id}/ledger`); setData(data); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (loading || !data) return <Loading />;
  const totalIn = data.rows.reduce((s, r) => s + (r.in || 0), 0);
  const totalOut = data.rows.reduce((s, r) => s + (r.out || 0), 0);
  const doExport = () => exportCSV(`${data.account.name}_ledger.csv`, data.rows.map((r) => ({
    Date: formatDate(r.txn_date), Txn: r.txn_number, Type: r.type, Description: r.description,
    In: r.in, Out: r.out, Balance: r.running,
  })));

  return (
    <div>
      <button onClick={() => navigate("/accounts")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-3"><ArrowLeft className="h-4 w-4" /> Back to Accounts</button>
      <PageHeader title={data.account.name} subtitle={`${data.account.type}${data.account.bank_name ? " · " + data.account.bank_name : ""}`}
        actions={<Button size="sm" variant="outline" onClick={doExport} className="gap-1.5"><Download className="h-4 w-4" /> Export</Button>} />
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <StatCard label="Opening Balance" value={data.opening_balance} accent="slate" />
        <StatCard label="Total Money In" value={totalIn} accent="emerald" />
        <StatCard label="Total Money Out" value={totalOut} accent="rose" />
        <StatCard label="Closing Balance" value={data.closing} accent="teal" testid="acc-closing" />
      </div>
      {data.rows.length === 0 ? <EmptyState title="No transactions in this account" /> : (
        <TableWrap>
          <thead><tr><Th>Date</Th><Th>Txn</Th><Th>Type</Th><Th>Description</Th><Th className="text-right">In</Th><Th className="text-right">Out</Th><Th className="text-right">Balance</Th></tr></thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40">
                <Td>{formatDate(r.txn_date)}</Td><Td className="font-mono">{r.txn_number}</Td>
                <Td>{r.type.replace(/_/g, " ")}</Td><Td>{r.description || "-"}</Td>
                <Td className="text-right font-mono text-emerald-600">{r.in ? formatINR(r.in) : "-"}</Td>
                <Td className="text-right font-mono text-rose-600">{r.out ? formatINR(r.out) : "-"}</Td>
                <Td className="text-right font-mono font-semibold">{formatINR(r.running)}</Td>
              </tr>
            ))}
          </tbody>
        </TableWrap>
      )}
    </div>
  );
}
